<script setup>
import DotsSixVerticalIcon from '@iconify-vue/ph/dots-six-vertical';
import PlusIcon from '@iconify-vue/ph/plus';
import TrashIcon from '@iconify-vue/ph/trash';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { ref } from 'vue';

const {
    modelValue,
    addLabel = 'Ajouter',
    emptyText = null,
    newItem = null,
    numbered = true,
    sortable = true,
    disabled = false,
} = defineProps({
    modelValue: {
        type: Array,
        required: true,
    },
    addLabel: {
        type: String,
        required: false,
    },
    emptyText: {
        type: String,
        required: false,
    },
    newItem: {
        type: Function,
        required: false,
    },
    numbered: {
        type: Boolean,
        required: false,
    },
    sortable: {
        type: Boolean,
        required: false,
    },
    disabled: {
        type: Boolean,
        required: false,
    },
});

const emit = defineEmits(['update:modelValue', 'add', 'remove']);

const dragIndex = ref(null);
const overIndex = ref(null);

const keyMap = new WeakMap();

let keyCounter = 0;

function keyFor(item) {
    if (item === null || typeof item !== 'object') {
        return item;
    }

    if (!keyMap.has(item)) {
        keyMap.set(item, ++keyCounter);
    }

    return keyMap.get(item);
}

function onDragStart(index, event) {
    if (disabled) {
        event.preventDefault();

        return;
    }

    dragIndex.value = index;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', String(index));

    const row = event.currentTarget.closest('[data-sortable-row]');

    if (row) {
        event.dataTransfer.setDragImage(row, 20, 20);
    }
}

function onDragOver(index) {
    if (dragIndex.value === null) {
        return;
    }

    overIndex.value = index;
}

function onDrop(index) {
    if (dragIndex.value !== null && dragIndex.value !== index) {
        const items = [...modelValue];
        const [moved] = items.splice(dragIndex.value, 1);
        items.splice(index, 0, moved);
        emit('update:modelValue', items);
    }

    resetDragState();
}

function onDragEnd() {
    resetDragState();
}

function resetDragState() {
    dragIndex.value = null;
    overIndex.value = null;
}

function add() {
    if (newItem) {
        emit('update:modelValue', [...modelValue, newItem()]);
    }

    emit('add');
}

function remove(index) {
    const items = [...modelValue];
    items.splice(index, 1);
    emit('update:modelValue', items);
    emit('remove', index);
}
</script>

<template>
    <div class="flex flex-col gap-4 mb-4">
        <div
            v-if="$slots.header || newItem"
            class="flex items-center justify-between gap-4"
        >
            <slot name="header" />

            <div class="ml-auto">
                <ActionButton
                    v-if="newItem && !disabled"
                    :callback="add"
                    :left-icon="PlusIcon"
                    size="small"
                >
                    {{ addLabel }}
                </ActionButton>
            </div>
        </div>

        <slot v-if="modelValue.length === 0" name="empty">
            <p
                v-if="emptyText"
                class="rounded border border-dashed border-gray-300 px-5 py-10 text-center text-sm text-gray-400"
            >
                {{ emptyText }}
            </p>
        </slot>

        <ul v-else class="flex flex-col gap-4">
            <li
                v-for="(item, index) in modelValue"
                :key="keyFor(item)"
                data-sortable-row
                class="flex items-start gap-4 rounded border bg-white p-4 duration-150"
                :class="[
                    dragIndex === index ? 'opacity-40' : 'border-gray-200',
                    overIndex === index && dragIndex !== index
                        ? 'border-gray-300 ring-1 ring-gray-300'
                        : 'border-gray-200',
                ]"
                @dragover.prevent="onDragOver(index)"
                @drop.prevent="onDrop(index)"
            >
                <div
                    v-if="sortable || numbered"
                    class="flex flex-col items-center gap-2"
                >
                    <div
                        v-if="sortable"
                        class="shrink-0 rounded p-1 text-gray-300 duration-150 hover:text-gray-500"
                        :class="
                            disabled
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-grab touch-none active:cursor-grabbing'
                        "
                        :draggable="!disabled"
                        role="button"
                        aria-label="Déplacer l'élément"
                        @dragstart="onDragStart(index, $event)"
                        @dragend="onDragEnd"
                    >
                        <DotsSixVerticalIcon class="size-5" />
                    </div>

                    <span
                        v-if="numbered"
                        class="flex size-6 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-500"
                    >
                        {{ index + 1 }}
                    </span>
                </div>

                <div class="min-w-0 flex-1">
                    <slot :item="item" :index="index" />
                </div>

                <ActionButton
                    :disabled="disabled"
                    aria-label="Supprimer l'élément"
                    :callback="() => remove(index)"
                    :icon="TrashIcon"
                    size="small"
                    type="danger"
                    square
                />
            </li>
        </ul>
    </div>
</template>
